import { useCallback, useState } from 'react';
import { shareChat, queryShareChatHistory } from '@src/services/community';
import type { AgentConversationShareDTO, ShareChatRequest } from '@src/services/community';

// ─── 类型 ─────────────────────────────────────────────────────────────────────

export interface ShareChatParams {
  marioAgentId?: string;
  sessionKey: string;
  /** 每条为 JSON 字符串：`{"role":"user"|"assistant"|"system","content":"..."}`，对应接口 messages */
  messages: string[];
}

export interface UseShareChatReturn {
  /** 正在执行分享请求 */
  sharing: boolean;
  /** 正在查询分享内容 */
  querying: boolean;
  /**
   * 发起分享，成功后返回 shareId。
   * 失败时抛出异常，调用方可自行 catch 处理。
   */
  share: (params: ShareChatParams) => Promise<string>;
  /**
   * 根据 shareId 查询分享内容，返回分享详情。
   * 失败时抛出异常，调用方可自行 catch 处理。
   */
  queryHistory: (shareId: string) => Promise<AgentConversationShareDTO>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useShareChat(): UseShareChatReturn {
  const [sharing, setSharing] = useState(false);
  const [querying, setQuerying] = useState(false);

  const share = useCallback(
    async (params: ShareChatParams): Promise<string> => {
      setSharing(true);
      try {
        const body: ShareChatRequest = {
          marioAgentId: params.marioAgentId,
          sessionKey: params.sessionKey,
          messages: params.messages,
        };
        const res = await shareChat({}, body, {
          extra: { timeout: 60_000 },
        });
        if (res?.code !== 0 || !res.data) {
          throw new Error(res?.message ?? '分享失败');
        }
        return res.data as string;
      } finally {
        setSharing(false);
      }
    },
    [],
  );

  const queryHistory = useCallback(
    async (shareId: string): Promise<AgentConversationShareDTO> => {
      setQuerying(true);
      try {
        const res = await queryShareChatHistory({ shareId });
        if (res?.code !== 0 || !res.data) {
          throw new Error(res?.message ?? '获取分享内容失败');
        }
        return res.data as AgentConversationShareDTO;
      } finally {
        setQuerying(false);
      }
    },
    [],
  );

  return { sharing, querying, share, queryHistory };
}
